import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import permissionService from "../../services/api/permissionService";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { QUERY_CONFIGS } from "../../lib/react-query/config";
import { useCallback } from "react";

// Permission Query Keys
export const permissionQueryKeys = {
  all: ["permissions"],
  list: () => [...permissionQueryKeys.all, "list"],
  detail: (id) => [...permissionQueryKeys.all, "detail", id],
  create: () => [...permissionQueryKeys.all, "create"],
  update: () => [...permissionQueryKeys.all, "update"],
  delete: () => [...permissionQueryKeys.all, "delete"],
};

export const useGetAllPermissionsQuery = () => {
  return useQuery({
    queryKey: permissionQueryKeys.list(),
    queryFn: permissionService.getAllPermissions,
    ...QUERY_CONFIGS.STATIC,
  });
};

// GET PERMISSION BY ID
export const useGetPermissionByIdQuery = (id, options = {}) => {
  const request = useCallback(async () => {
    try {
      const response = await permissionService.getPermissionById(id);
      return response;
    } catch (error) {
      const errorMessage = extractAxiosErrorMessage(
        error,
        "Failed to fetch permission details",
      );
      throw new Error(errorMessage);
    }
  }, [id]);

  return useQuery({
    queryKey: permissionQueryKeys.detail(id),
    queryFn: request,
    enabled: !!id && options.enabled !== false,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useCreatePermissionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (permissionData) =>
      permissionService.createPermission(permissionData),
    mutationKey: permissionQueryKeys.create(),
    // Optimistic update
    onMutate: async (newPermission) => {
      await queryClient.cancelQueries({ queryKey: permissionQueryKeys.list() });

      // Snapshot current cache
      const previousPermissions = queryClient.getQueryData(
        permissionQueryKeys.list(),
      );

      // Optimistically update cache with new permission
      queryClient.setQueryData(permissionQueryKeys.list(), (old) => {
        if (!old) return [newPermission];
        return [...old, { ...newPermission, id: `temp-${Date.now()}` }];
      });

      // Return rollback data
      return { previousPermissions };
    },

    //Rollback on error
    onError: (error, _newPermission, context) => {
      if (context?.previousPermissions) {
        queryClient.setQueryData(
          permissionQueryKeys.list(),
          context.previousPermissions,
        );
      }
    },

    //Always refetch after mutation settles
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: permissionQueryKeys.list() });
    },

    retry: false,
  });
};

export const useUpdatePermissionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissionData }) =>
      permissionService.updatePermission(id, permissionData),
    mutationKey: permissionQueryKeys.update(),

    // Optimistic update
    onMutate: async ({ id, permissionData }) => {
      await queryClient.cancelQueries({ queryKey: permissionQueryKeys.list() });

      // Snapshot current cache
      const previousPermissions = queryClient.getQueryData(
        permissionQueryKeys.list(),
      );

      // Optimistically update cache
      queryClient.setQueryData(permissionQueryKeys.list(), (old) => {
        if (!old) return [];
        return old.map((perm) =>
          perm.id === id ? { ...perm, ...permissionData } : perm,
        );
      });

      // Return rollback data
      return { previousPermissions };
    },

    // Rollback on error
    onError: (error, _variables, context) => {
      if (context?.previousPermissions) {
        queryClient.setQueryData(
          permissionQueryKeys.list(),
          context.previousPermissions,
        );
      }
    },

    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: permissionQueryKeys.list() });
      queryClient.invalidateQueries({
        queryKey: permissionQueryKeys.detail(variables.id),
      });
    },

    retry: false,
  });
};

export const useDeletePermissionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => permissionService.deletePermission(id),
    mutationKey: permissionQueryKeys.delete(),

    // Optimistic update
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: permissionQueryKeys.list() });

      // Snapshot current data
      const previousPermissions = queryClient.getQueryData(
        permissionQueryKeys.list(),
      );

      // Optimistically remove deleted permission
      queryClient.setQueryData(permissionQueryKeys.list(), (old) => {
        if (!old) return [];
        return old.filter((perm) => perm.id !== id);
      });

      // Return rollback context
      return { previousPermissions };
    },

    // Rollback on error
    onError: (error, _id, context) => {
      if (context?.previousPermissions) {
        queryClient.setQueryData(
          permissionQueryKeys.list(),
          context.previousPermissions,
        );
      }
    },

    // Always refetch or clean up after
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: permissionQueryKeys.list() });
      queryClient.removeQueries({
        queryKey: permissionQueryKeys.detail(id),
      });
    },

    retry: false,
  });
};
